import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { SupplierForBuyer, formatMoney, roundTo, DEFAULT_AGING_RANGES } from '@dill/dill-shared';
import distributorPlaceholder from '../../../../assets/svg/distributor_placeholder.svg';
import emptyListImg from '../../../../assets/images/emptyList.png';
import {
  FileText,
  Receipt,
  CurrencyDollar,
  ArrowRight,
  Calendar,
  Buildings,
} from '@phosphor-icons/react';
import AppDateRangePickerModal from '../../../general/AppDateRangePickerModal/AppDateRangePickerModal';
import { PayByProjectModal } from './PayByProjectModal';
import CreditUtilizationChart from './CreditUtilizationChart';
import DSOTrendChart from './DSOTrendChart';
import InvoiceVolumeChart from './InvoiceVolumeChart';
import type { InvoiceVolumePoint, DSOTrendPoint, CreditUtilizationPoint } from './chartTypes';
import { updateSelectedSupplierForBuyer } from '../../../../redux/globalSlices/invoiceSlice';
import {
  getBuyerStatementPeriods,
  getGroupedBuyersSuppliers,
  fetchBuyerMonthlyAnalytics,
} from '../../../../redux/services/buyersService';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { useParamMainBuyerId } from '../../../../utils/hooks/useParamMainBuyerId';
import { AddFixedAmountPaymentsModal } from '../../fixedAmountPayments/AddFixedAmountPaymentsModal';
import { useBuyerUserPermissions } from '../../../../utils/hooks/useBuyerUserPermissions';
import { Tooltip as ReactTooltip } from 'react-tooltip';

export const MainBuyerDashboard = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    selectedBuyerSuppliers,
    loadingList: buyersLoadingList,
    statementPeriods,
    monthlyAnalytics,
    loadingMonthlyAnalytics,
  } = useAppSelector((state) => state.buyers);
  const { user } = useAppSelector((state) => state.auth);

  const { buyerUserCanPay } = useBuyerUserPermissions();
  const [suppliers, setSuppliers] = useState<SupplierForBuyer[]>([]);
  const [isFixedAmountModalOpen, setIsFixedAmountModalOpen] = useState<boolean>(false);
  const [isPayByProjectModalOpen, setIsPayByProjectModalOpen] = useState<boolean>(false);
  const [fetchedMonths, setFetchedMonths] = useState(12);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1),
    endDate: new Date(),
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedMainBuyerId] = useParamMainBuyerId();
  const { isPreviewMode } = useAppSelector((state) => state.preview);

  useEffect(() => {
    if (selectedBuyerSuppliers.length > 0) {
      dispatch(updateSelectedSupplierForBuyer(selectedBuyerSuppliers[0]));
    }
  }, [selectedBuyerSuppliers]);

  useEffect(() => {
    setSuppliers(selectedBuyerSuppliers);
  }, [selectedBuyerSuppliers]);

  useEffect(() => {
    if (selectedMainBuyerId) {
      dispatch(
        getGroupedBuyersSuppliers({
          mainBuyerId: selectedMainBuyerId,
          weeksLimit: 2,
        })
      );
      dispatch(getBuyerStatementPeriods({ mainBuyerId: selectedMainBuyerId }));
    }
  }, [selectedMainBuyerId]);

  useEffect(() => {
    if (selectedMainBuyerId && user?.userMainBuyers && !isPreviewMode) {
      const mainBuyer = user?.userMainBuyers
        .filter((mb) => !mb?.isDeactivated && !mb?.isCODCustomer)
        .find((mb) => mb.id === selectedMainBuyerId);
      if (!mainBuyer) {
        navigate('/companies');
      }
    }
  }, [user?.userMainBuyers, isPreviewMode]);

  useEffect(() => {
    if (selectedMainBuyerId && user?.userMainBuyers) {
      const mainBuyer = user?.userMainBuyers.find((mb) => mb.id === selectedMainBuyerId);
      if (!mainBuyer) {
        navigate('/companies');
      }
    }
  }, [user?.userMainBuyers]);

  // ── Single supplier ──────────────────────────────────────────────
  const supplier = suppliers[0];
  const buyerId = supplier?.buyer?.id;

  const hasJobs = useMemo(() => {
    const allInvoices = [
      ...(supplier?.invoices?.pastDueInvoices?.invoices || []),
      ...(supplier?.invoices?.currentInvoices?.invoices || []),
    ];
    return allInvoices.some((inv) => inv.jobId && inv.quickBooksJob);
  }, [supplier?.invoices]);

  useEffect(() => {
    if (selectedMainBuyerId && buyerId) {
      dispatch(
        fetchBuyerMonthlyAnalytics({
          mainBuyerId: selectedMainBuyerId,
          buyerId,
          months: fetchedMonths,
        })
      );
    }
  }, [selectedMainBuyerId, buyerId, fetchedMonths]);

  useEffect(() => {
    const now = new Date();
    const required =
      (now.getFullYear() - dateRange.startDate.getFullYear()) * 12 +
      (now.getMonth() - dateRange.startDate.getMonth()) +
      1;
    if (required > fetchedMonths) {
      setFetchedMonths(required);
    }
  }, [dateRange.startDate, fetchedMonths]);

  const totalBalance = supplier
    ? parseFloat(supplier?.buyer?.documentsSummary?.totalAmountDueInvoices?.toString() || '0')
    : 0;

  // ── Last Statement ───────────────────────────────────────────────
  const lastStatementId =
    statementPeriods && statementPeriods.length > 0
      ? [...statementPeriods].sort(
          (a, b) => new Date(b.statementDate).getTime() - new Date(a.statementDate).getTime()
        )[0].id
      : null;

  // ── Credit Overview ──────────────────────────────────────────────
  const creditLimit = Number(supplier?.buyer?.creditAmount ?? 0);
  const creditTerms = supplier?.buyer?.terms || '—';
  const utilizationPercent = creditLimit > 0 ? Math.round((totalBalance / creditLimit) * 100) : 0;
  const isOverCreditLimit = utilizationPercent > 100;

  // ── Aging rows ───────────────────────────────────────────────────
  const buildAgingRows = () => {
    if (!supplier) return [];
    const rows: { label: string; amount: number }[] = [];

    // Current Due
    rows.push({
      label: 'Current Due',
      amount: parseFloat(
        supplier?.buyer?.documentsSummary?.totalCurrentDueInvoices?.toString() || '0'
      ),
    });

    const isUseAgingRanges = supplier?.isUseAgingRanges ?? false;
    if (isUseAgingRanges) {
      const agingRanges =
        supplier?.agingRanges && supplier.agingRanges.length > 0
          ? supplier.agingRanges
          : DEFAULT_AGING_RANGES;
      const sortedRanges = [...agingRanges].sort((a, b) => a.order - b.order);
      const agingBuckets = supplier?.buyer?.documentsSummary?.agingBuckets;

      sortedRanges.forEach((range) => {
        const bucket = Array.isArray(agingBuckets)
          ? agingBuckets.find((b) => b.order === range.order)
          : undefined;
        rows.push({
          label: `${range.label} Days Past Due`,
          amount: bucket?.amount ?? 0,
        });
      });
    } else {
      rows.push({
        label: '1 - 30 Days Past Due',
        amount: parseFloat(
          supplier?.buyer?.documentsSummary?.totalPastDue1To30Invoices?.toString() || '0'
        ),
      });
      rows.push({
        label: '31 - 60 Days Past Due',
        amount: parseFloat(
          supplier?.buyer?.documentsSummary?.totalPastDue31To60Invoices?.toString() || '0'
        ),
      });
      rows.push({
        label: '61 - 90 Days Past Due',
        amount: parseFloat(
          supplier?.buyer?.documentsSummary?.totalPastDue61To90Invoices?.toString() || '0'
        ),
      });
      rows.push({
        label: 'Over 90 Days Past Due',
        amount: parseFloat(
          supplier?.buyer?.documentsSummary?.totalPastDueOver90Invoices?.toString() || '0'
        ),
      });
    }

    return rows;
  };

  const agingRows = buildAgingRows();

  // ── Chart data derived from monthly analytics ───────────────────
  const paddedAnalytics = useMemo(() => {
    const now = new Date();
    const existing = new Map(monthlyAnalytics.map((a) => [a.month, a]));
    const months: string[] = [];
    let cur = new Date(dateRange.startDate.getFullYear(), dateRange.startDate.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 1);
    while (cur <= end) {
      months.push(`${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}`);
      cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
    }
    const empty = (month: string) => {
      const [y, m] = month.split('-');
      const yr = parseInt(y);
      const mo = parseInt(m);
      return {
        id: month,
        buyerId: '',
        supplierId: '',
        mainBuyerId: null,
        month,
        year: y,
        periodStart: new Date(yr, mo - 1, 1),
        periodEnd: new Date(yr, mo, 0),
        lastComputedAt: new Date(0),
        creditLimit: 0,
        openAR: 0,
        utilizationPercent: 0,
        totalInvoiced: 0,
        invoiceCount: 0,
        totalPaid: 0,
        dso: 0,
        dsoPaymentCount: 0,
        createdAt: new Date(0),
        updatedAt: new Date(0),
      };
    };
    return months.map((key) => existing.get(key) ?? empty(key));
  }, [monthlyAnalytics, dateRange.startDate]);

  const sortedAnalytics = useMemo(
    () => [...paddedAnalytics].sort((a, b) => a.month.localeCompare(b.month)),
    [paddedAnalytics]
  );

  const filteredAnalytics = useMemo(() => {
    const startMonth = moment(dateRange.startDate).format('YYYY-MM');
    const endMonth = moment(dateRange.endDate).format('YYYY-MM');
    return sortedAnalytics.filter((a) => a.month >= startMonth && a.month <= endMonth);
  }, [sortedAnalytics, dateRange]);

  const toChartMonth = (ym: string) => {
    const [y, m] = ym.split('-');
    return `${y}-${new Date(parseInt(y), parseInt(m) - 1, 1)
      .toLocaleString('en-US', { month: 'short' })
      .toUpperCase()}`;
  };

  const invoiceVolumeData: InvoiceVolumePoint[] = useMemo(
    () =>
      filteredAnalytics.map((a) => ({
        month: toChartMonth(a.month),
        totalInvoiced: a.totalInvoiced,
      })),
    [filteredAnalytics]
  );

  const dsoTrendData: DSOTrendPoint[] = useMemo(
    () =>
      filteredAnalytics.map((a) => ({
        month: toChartMonth(a.month),
        dso: a.dso,
      })),
    [filteredAnalytics]
  );

  const creditUtilizationData: CreditUtilizationPoint[] = useMemo(
    () =>
      filteredAnalytics.map((a) => ({
        month: toChartMonth(a.month),
        openAR: a.openAR,
        creditLimit: a.creditLimit,
        utilizationPercent: a.utilizationPercent,
      })),
    [filteredAnalytics]
  );

  const lastEntry = filteredAnalytics[filteredAnalytics.length - 1];
  // Invoice volume is a flow, so the headline is the total over the selected range (YTD by
  // default); DSO and utilization are point-in-time, so they show the latest month.
  const totalInvoiceVolume = filteredAnalytics.reduce((sum, a) => sum + (a.totalInvoiced || 0), 0);
  const latestDSO = lastEntry?.dso ?? 0;
  const latestUtilizationPct = lastEntry?.utilizationPercent ?? 0;

  const formatDateRangeDisplay = () => {
    const start = moment(dateRange.startDate).format('MMM DD, YYYY');
    const end = moment(dateRange.endDate).format('MMM DD, YYYY');
    return `${start} – ${end}`;
  };

  // ── Loading skeleton ─────────────────────────────────────────────
  if (buyersLoadingList && !supplier) {
    return (
      <div className="h-full w-full">
        <div className="w-full h-full overflow-y-scroll pb-8 flex flex-col gap-4">
          {/* Header skeleton */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 md:px-6 md:py-5 animate-pulse">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-gray-200" />
                <div className="space-y-2">
                  <div className="h-4 w-32 rounded bg-gray-200" />
                  <div className="h-3 w-24 rounded bg-gray-100" />
                </div>
              </div>
              <div className="text-right space-y-2">
                <div className="h-3 w-20 rounded bg-gray-100" />
                <div className="h-6 w-28 rounded bg-gray-200" />
              </div>
            </div>
          </div>
          {/* Aging skeleton */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 md:px-6 md:py-5 animate-pulse">
            <div className="h-3 w-24 rounded bg-gray-200 mb-3" />
            <div className="space-y-1.5">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-9 rounded-lg bg-gray-100" />
              ))}
              <div className="h-9 rounded-lg bg-gray-50" />
            </div>
          </div>
          {/* Credit overview skeleton */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 md:px-6 md:py-5 animate-pulse">
            <div className="h-3 w-28 rounded bg-gray-200 mb-3" />
            <div className="h-4 w-full rounded-full bg-gray-100" />
          </div>
          {/* Payment cards skeleton */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 md:px-6 md:py-5 animate-pulse">
            <div className="h-3 w-28 rounded bg-gray-200 mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-20 rounded-xl bg-gray-100" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Empty state ──────────────────────────────────────────────────
  if (suppliers.length < 1) {
    return (
      <div className="h-full w-full flex flex-col bg-white items-center justify-center">
        <img className="w-1/2" src={emptyListImg} alt="" />
        <p className="font-semibold w-1/2 text-center">
          Your suppliers have not yet signed up for Dill invoicing & payments. If you would like to
          get automated lien waivers with payments, get your suppliers on Dill by reaching out to
          customer support.
        </p>
      </div>
    );
  }

  // ── Dashboard ────────────────────────────────────────────────────
  return (
    <div className="h-full w-full p-4">
      <AddFixedAmountPaymentsModal
        open={isFixedAmountModalOpen}
        handleClose={() => setIsFixedAmountModalOpen(false)}
      />
      {supplier && (
        <PayByProjectModal
          open={isPayByProjectModalOpen}
          onClose={() => setIsPayByProjectModalOpen(false)}
          supplier={supplier}
          onContinue={(jobIds) => {
            dispatch(updateSelectedSupplierForBuyer(supplier));
            navigate(
              `/companies/${selectedMainBuyerId}/invoices/${supplier.domainId}?jobIds=${jobIds.join(
                ','
              )}`
            );
            setIsPayByProjectModalOpen(false);
          }}
        />
      )}

      <div className="w-full h-full overflow-y-scroll pb-8 flex flex-col gap-5 md:gap-4">
        {/* ── Section 1: Balance Header ──────────────────────────────── */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:px-6 md:py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
              {supplier?.logo?.url ? (
                <img className="h-8 w-8 object-contain" src={supplier.logo.url} alt="" />
              ) : (
                <img className="h-6 w-6" src={distributorPlaceholder} alt="" />
              )}
            </div>
            <div>
              <h1 className="text-sm font-semibold text-gray-900">{supplier.name}</h1>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
              Total Balance
            </p>
            <p className="text-xl md:text-2xl font-semibold text-gray-900 tabular-nums tracking-tight">
              {formatMoney(totalBalance, 2)}
            </p>
          </div>
        </div>

        {/* ── Section 2: Aging Summary ────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:px-6 md:py-5">
          <h2 className="text-[10px] uppercase tracking-wider text-gray-500 font-medium mb-3">
            Aging Summary
          </h2>
          <div className="space-y-1.5">
            {agingRows.map((row, i) => {
              const agingStyle = [
                'bg-emerald-50 border-l-emerald-500 text-emerald-700',
                'bg-amber-50 border-l-amber-500 text-amber-700',
                'bg-orange-50 border-l-orange-500 text-orange-700',
                'bg-red-50 border-l-red-500 text-red-700',
                'bg-rose-50 border-l-rose-600 text-rose-700',
              ][Math.min(i, 4)];

              return (
                <div
                  key={row.label}
                  className={`flex items-center justify-between py-2 px-3 rounded-lg border-l-[3px] ${agingStyle}`}>
                  <span className="text-sm font-medium">{row.label}</span>
                  <span className="text-sm font-medium tabular-nums">
                    {formatMoney(row.amount, 2)}
                  </span>
                </div>
              );
            })}
            <div className="flex items-center justify-between py-2 px-3 rounded-lg bg-gray-50 border-l-[3px] border-l-gray-400">
              <span className="text-sm font-semibold text-gray-700">Total Amount Due</span>
              <span className="text-sm font-medium text-gray-900 tabular-nums">
                {formatMoney(totalBalance, 2)}
              </span>
            </div>
          </div>
        </div>
        {/* ── Section 3: Make a Payment ────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 md:px-6 md:py-5">
          <h2 className="text-[10px] uppercase tracking-wider text-gray-500 font-medium mb-4">
            Make a Payment
          </h2>

          <div
            className={`grid grid-cols-1 ${hasJobs ? 'md:grid-cols-4' : 'md:grid-cols-3'} gap-3`}>
            {/* Pay Open Invoices — primary CTA (solid Dill blue) */}
            <div
              className={`rounded-xl border-2 shadow-sm transition-colors p-4 ${
                buyerUserCanPay
                  ? 'bg-[#3762FB] hover:bg-[#2847B7] border-[#3762FB] hover:border-[#2847B7] hover:shadow cursor-pointer group'
                  : 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-60'
              }`}
              data-tooltip-id={!buyerUserCanPay ? 'no_pay_permission' : undefined}
              onClick={() => {
                if (!buyerUserCanPay) return;
                dispatch(updateSelectedSupplierForBuyer(supplier));
                navigate(`/companies/${selectedMainBuyerId}/invoices/${supplier.domainId}`);
              }}>
              <div className="flex items-start gap-3">
                <div
                  className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
                    buyerUserCanPay ? 'bg-white/15' : 'bg-gray-200'
                  }`}>
                  <FileText
                    size={20}
                    className={buyerUserCanPay ? 'text-white' : 'text-gray-400'}
                    weight="duotone"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p
                      className={`text-sm font-semibold ${
                        buyerUserCanPay ? 'text-white' : 'text-gray-500'
                      }`}>
                      Pay Open Invoices
                    </p>
                    {buyerUserCanPay && (
                      <ArrowRight
                        size={16}
                        className="text-white opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all shrink-0"
                      />
                    )}
                  </div>
                  <p
                    className={`text-xs mt-1 ${
                      buyerUserCanPay ? 'text-blue-100' : 'text-gray-400'
                    }`}>
                    Select and pay individual open invoices
                  </p>
                </div>
              </div>
            </div>

            {/* Pay Last Statement — secondary (neutral gray) */}
            <div
              className={`bg-white rounded-xl border border-gray-200 transition-colors p-4 ${
                lastStatementId
                  ? 'hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                  : 'opacity-50 cursor-not-allowed'
              }`}
              onClick={() => {
                if (!lastStatementId) return;
                dispatch(updateSelectedSupplierForBuyer(supplier));
                navigate(
                  `/companies/${selectedMainBuyerId}/statementInvoices?statementId=${lastStatementId}`
                );
              }}>
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                  <Receipt size={20} className="text-gray-500" weight="duotone" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Pay Last Statement</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Pay the remaining balance from your last statement
                  </p>
                </div>
              </div>
            </div>

            {/* Pay Fixed Amount — secondary (neutral gray) */}
            <div
              className={`bg-white rounded-xl border border-gray-200 transition-colors p-4 ${
                buyerUserCanPay
                  ? 'hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                  : 'opacity-50 cursor-not-allowed'
              }`}
              data-tooltip-id={!buyerUserCanPay ? 'no_pay_permission' : undefined}
              onClick={() => {
                if (!buyerUserCanPay) return;
                dispatch(updateSelectedSupplierForBuyer(supplier));
                setIsFixedAmountModalOpen(true);
              }}>
              <div className="flex items-start gap-3">
                <div className="h-9 w-9 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                  <CurrencyDollar size={20} className="text-gray-500" weight="duotone" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">Pay Fixed Amount</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Make a custom payment towards your balance
                  </p>
                </div>
              </div>
            </div>

            {/* Pay by Project — secondary (neutral gray) */}
            {hasJobs && (
              <div
                className={`bg-white rounded-xl border border-gray-200 transition-colors p-4 ${
                  buyerUserCanPay
                    ? 'hover:border-gray-300 hover:bg-gray-50 cursor-pointer'
                    : 'opacity-50 cursor-not-allowed'
                }`}
                data-tooltip-id={!buyerUserCanPay ? 'no_pay_permission' : undefined}
                onClick={() => {
                  if (!buyerUserCanPay) return;
                  setIsPayByProjectModalOpen(true);
                }}>
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                    <Buildings size={20} className="text-gray-500" weight="duotone" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Pay by Project</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Select jobs and pay all their open invoices
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Section 4: Credit Overview ───────────────────────────────── */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">
            {/* Credit Limit */}
            <div className="px-4 py-4 md:px-6 md:py-5">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
                Credit Limit
              </p>
              <p className="text-xl font-semibold text-gray-900 tabular-nums mt-1">
                {formatMoney(creditLimit, 2)}
              </p>
            </div>

            {/* Terms */}
            <div className="px-4 py-4 md:px-6 md:py-5">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
                Terms
              </p>
              <p className="text-xl font-semibold text-gray-900 mt-1">{creditTerms}</p>
            </div>

            {/* Credit Utilization */}
            <div className="px-4 py-4 md:px-6 md:py-5">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
                Credit Utilization
              </p>
              <div className="flex items-center gap-2 mt-1">
                <p
                  className={`text-xl font-semibold tabular-nums ${
                    isOverCreditLimit ? 'text-red-600' : 'text-gray-900'
                  }`}>
                  {utilizationPercent}%
                </p>
                {isOverCreditLimit && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-700">
                    Over Limit
                  </span>
                )}
              </div>
              <div className="mt-2 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full ${
                    isOverCreditLimit ? 'bg-red-500' : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(utilizationPercent, 100)}%` }}
                />
              </div>
            </div>
          </div>
        </div>
        {/* ── Section 5: Charts Grid ───────────────────────────────────── */}
        <div className="flex justify-end">
          <button
            onClick={() => setIsDatePickerOpen(true)}
            className="inline-flex items-center gap-2 h-[34px] px-3.5 text-xs font-medium rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 cursor-pointer transition-colors whitespace-nowrap">
            <Calendar size={16} weight="bold" />
            <span>{formatDateRangeDisplay()}</span>
          </button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-5">
          {/* Invoice Volume */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 md:px-6 md:py-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
                Invoice Volume
              </h3>
              <span className="text-sm font-medium text-gray-900 tabular-nums">
                {formatMoney(totalInvoiceVolume, 0)}
              </span>
            </div>
            <InvoiceVolumeChart data={invoiceVolumeData} />
          </div>

          {/* DSO Trend */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 md:px-6 md:py-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
                DSO Trend
              </h3>
              <span className="text-sm font-medium text-gray-900 tabular-nums">
                {latestDSO} days
              </span>
            </div>
            <DSOTrendChart data={dsoTrendData} />
          </div>

          {/* Credit Utilization */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 md:px-6 md:py-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[10px] uppercase tracking-wider text-gray-500 font-medium">
                Credit Utilization
              </h3>
              <span className="text-sm font-medium text-gray-900 tabular-nums">
                {latestUtilizationPct.toFixed(1)}%
              </span>
            </div>
            <CreditUtilizationChart data={creditUtilizationData} />
          </div>
        </div>
      </div>
      <AppDateRangePickerModal
        open={isDatePickerOpen}
        handleClose={() => setIsDatePickerOpen(false)}
        onSelectDateRange={(range) => {
          setDateRange({
            startDate: range.startDate ?? dateRange.startDate,
            endDate: range.endDate ?? dateRange.endDate,
          });
          setIsDatePickerOpen(false);
        }}
        selectedDateRange={dateRange}
      />
      <ReactTooltip
        id={'no_pay_permission'}
        place="bottom"
        variant="dark"
        opacity={'100%'}
        style={{
          background: '#222',
          width: '300px',
          zIndex: 99999,
        }}>
        <span>You don't have permissions to make payments. Please contact your account admin.</span>
      </ReactTooltip>
    </div>
  );
};
